import React, { useState, useEffect } from "react";
import { db } from "../db/firebase";
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import UserInfoModal from "../components/UserInfoModal";
import { AnimatedModal } from "../components/AnimatedModal";
import { HiOutlineXMark, HiOutlineUserPlus } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

interface InviteUserProps {
  boardId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  color?: string;
}

const InviteUserModal: React.FC<InviteUserProps> = ({
  boardId,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [existingUserIds, setExistingUserIds] = useState<string[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!organizationId) {
        setUsers([]);
        return;
      }
      try {
        const q = query(
          collection(db, "users"),
          where("organizationId", "==", organizationId),
        );
        const querySnapshot = await getDocs(q);
        const usersData = querySnapshot.docs.map(
          (userDoc) =>
            ({
              id: userDoc.id,
              ...userDoc.data(),
            }) as User,
        );
        setUsers(usersData);
      } catch (error) {
        console.error(t("errorFetchingUsers"), error);
      }
    };

    const fetchExistingMemberships = async () => {
      try {
        const membershipsCollection = collection(db, "memberships");
        const membershipsQuery = query(
          membershipsCollection,
          where("boardId", "==", boardId)
        );
        const membershipsSnapshot = await getDocs(membershipsQuery);
        const existingUserIdsData = membershipsSnapshot.docs.map(
          (doc) => doc.data().userId
        );

        const userPromises = membershipsSnapshot.docs.map(async (doc) => {
          const membershipData = doc.data();
          const userDoc = await getDocs(
            query(
              collection(db, "users"),
              where("__name__", "==", membershipData.userId)
            )
          );
          if (userDoc.docs.length > 0) {
            const userData = userDoc.docs[0].data() as User;
            return {
              id: membershipData.userId,
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              color: membershipData.color, // Add color from the membership data
            };
          }
          return null;
        });

        const usersData = (await Promise.all(userPromises)).filter(
          (user) => user !== null
        ) as User[];
        setMembers(usersData);
        setExistingUserIds(existingUserIdsData);
      } catch (error) {
        console.error(t("errorFetchingMemberships"), error);
      }
    };

    if (isOpen) {
      fetchUsers();
      fetchExistingMemberships();
    }
  }, [isOpen, boardId, organizationId, t]);

  const handleInvite = async (user: User) => {
    if (existingUserIds.includes(user.id)) {
      setErrorMessage(t("userAlreadyMember"));
      return;
    }

    const color = getRandomColor();

    try {
      await addDoc(collection(db, "memberships"), {
        boardId,
        userId: user.id,
        role: "member",
        color, // Add the color to the membership document
      });
      setMembers([...members, { ...user, color }]);
      setExistingUserIds([...existingUserIds, user.id]);
    } catch (error) {
      console.error(t("errorInvitingUser"), error);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  const getRandomColor = () => {
    const colors = [
      "bg-red-500",
      "bg-green-500",
      "bg-blue-500",
      "bg-yellow-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      // Remove user from memberships collection
      const membershipsCollection = collection(db, "memberships");
      const membershipsQuery = query(
        membershipsCollection,
        where("boardId", "==", boardId),
        where("userId", "==", userId)
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);
      membershipsSnapshot.docs.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });

      // Remove user from assignedMembers in all cards
      const listsCollection = collection(db, "boards", boardId, "lists");
      const listsSnapshot = await getDocs(listsCollection);
      const listPromises = listsSnapshot.docs.map(async (listDoc) => {
        const cardsCollection = collection(
          db,
          "boards",
          boardId,
          "lists",
          listDoc.id,
          "cards"
        );
        const cardsSnapshot = await getDocs(cardsCollection);
        const cardPromises = cardsSnapshot.docs.map(async (cardDoc) => {
          const cardData = cardDoc.data();
          if (
            cardData.assignedMembers &&
            cardData.assignedMembers.includes(userId)
          ) {
            const updatedMembers = cardData.assignedMembers.filter(
              (id: string) => id !== userId
            );
            await updateDoc(
              doc(
                db,
                "boards",
                boardId,
                "lists",
                listDoc.id,
                "cards",
                cardDoc.id
              ),
              {
                assignedMembers: updatedMembers,
              }
            );
          }
        });
        await Promise.all(cardPromises);
      });
      await Promise.all(listPromises);

      setMembers(members.filter((member) => member.id !== userId));
      setExistingUserIds(existingUserIds.filter((id) => id !== userId));
    } catch (error) {
      console.error(t("errorRemovingUser"), error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatedModal isOpen onClose={onClose} backdropClassName="bg-black/60 backdrop-blur-sm">
        <div className="relative w-full max-w-md rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
                <HiOutlineUserPlus className="w-5 h-5" strokeWidth={2} />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {t("addNewMember")}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 overflow-y-auto space-y-4">
            <div className="flex flex-wrap gap-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className={`${member.color || "bg-[#5932EA]"} text-white w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-[#5932EA]/50 transition-all`}
                  onClick={() => {
                    setSelectedUser(member);
                    setIsUserInfoModalOpen(true);
                  }}
                  title={`${member.firstName} ${member.lastName}`}
                >
                  {getInitials(member.firstName, member.lastName)}
                </div>
              ))}
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("searchByEmail")}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#5932EA]/30"
            />
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {users
                .filter((user) => user.email === searchQuery)
                .map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-gray-700 ${
                      existingUserIds.includes(user.id) ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-[#5932EA] text-white w-9 h-9 rounded-full flex items-center justify-center shrink-0">
                        {getInitials(user.firstName, user.lastName)}
                      </div>
                      <span className="text-gray-800 dark:text-gray-200">
                        {user.firstName} {user.lastName}
                        {existingUserIds.includes(user.id) && ` (${t("member")})`}
                      </span>
                    </div>
                    {!existingUserIds.includes(user.id) && (
                      <button
                        onClick={() => handleInvite(user)}
                        className="py-2 px-4 rounded-xl text-sm font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors"
                      >
                        {t("invite")}
                      </button>
                    )}
                  </div>
                ))}
            </div>
            {errorMessage && (
              <p className="text-red-500 dark:text-red-400 text-sm">{errorMessage}</p>
            )}
          </div>
        </div>
      </AnimatedModal>
      {selectedUser && (
        <UserInfoModal
          user={selectedUser}
          isOpen={isUserInfoModalOpen}
          onClose={() => setIsUserInfoModalOpen(false)}
          boardId={boardId}
          onRemoveUser={handleRemoveUser}
        />
      )}
    </>
  );
};

export default InviteUserModal;
